import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";

const fundingChildren = [
  { to: "/funding", label: "Funding & Partnerships" },
  { to: "/funding/projects", label: "Preparing Projects for Funding" },
  { to: "/funding/beyond-grants", label: "Beyond Grants" },
  { to: "/capital-partners", label: "Bioregional Capital Partners" },
];

const navItems = [
  { to: "/our-work", label: "Our Work" },
  { to: "/funding", label: "Funding", children: fundingChildren },
  { to: "/approach", label: "Approach" },
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

const linkClasses = ({ isActive }) =>
  `inline-block text-sm tracking-wide transition-colors duration-300 border-b-2 pb-1 ${
    isActive
      ? "text-ochre-dark border-ochre"
      : "text-charcoal border-transparent hover:text-ochre-dark"
  }`;

const slug = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Header = () => {
  const [open, setOpen] = useState(false);
  const [fundingOpen, setFundingOpen] = useState(false);
  const location = useLocation();
  const fundingActive = fundingChildren.some((c) => location.pathname.startsWith(c.to));

  return (
    <header className="sticky top-0 z-50 bg-cream border-b border-charcoal/15">
      <div className="max-w-6xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link
          to="/"
          data-testid="header-logo-link"
          aria-label="Bioregional Ventures — home"
          className="flex items-center"
        >
          <img
            src="/logo.webp"
            alt="Bioregional Ventures"
            className="h-11 md:h-14 w-auto"
            data-testid="header-logo-image"
          />
        </Link>

        <nav aria-label="Main navigation" className="hidden md:block">
          <ul className="flex items-center gap-6 lg:gap-8">
            {navItems.map((item) =>
              item.children ? (
                <li
                  key={item.to}
                  className="relative"
                  onMouseEnter={() => setFundingOpen(true)}
                  onMouseLeave={() => setFundingOpen(false)}
                  onKeyDown={(e) => e.key === "Escape" && setFundingOpen(false)}
                >
                  <div className="flex items-center gap-1">
                    <NavLink
                      to={item.to}
                      className={() =>
                        `text-sm tracking-wide transition-colors duration-300 border-b-2 pb-1 ${
                          fundingActive
                            ? "text-ochre-dark border-ochre"
                            : "text-charcoal border-transparent hover:text-ochre-dark"
                        }`
                      }
                      data-testid="nav-funding-link"
                    >
                      {item.label}
                    </NavLink>
                    <button
                      type="button"
                      data-testid="nav-funding-dropdown-button"
                      aria-expanded={fundingOpen}
                      aria-haspopup="true"
                      aria-label="Open funding menu"
                      onClick={() => setFundingOpen((v) => !v)}
                      onFocus={() => setFundingOpen(true)}
                      className="ml-0.5 h-6 w-6 flex items-center justify-center self-center leading-none text-charcoal hover:text-ochre-dark transition-colors duration-300"
                    >
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-300 ${fundingOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>
                  {fundingOpen && (
                    <div className="absolute left-0 top-full pt-2 z-50">
                      <ul
                        data-testid="nav-funding-menu"
                        className="min-w-64 bg-cream border border-charcoal/15 shadow-lg py-2"
                      >
                        {item.children.map((child) => (
                          <li key={child.to}>
                            <NavLink
                              to={child.to}
                              end={child.to === "/funding"}
                              onClick={() => setFundingOpen(false)}
                              data-testid={`nav-funding-child-${slug(child.label)}`}
                              className={({ isActive }) =>
                                `block px-5 py-2.5 text-sm transition-colors duration-300 ${
                                  isActive
                                    ? "text-ochre-dark"
                                    : "text-charcoal hover:text-ochre-dark hover:bg-sage/40"
                                }`
                              }
                            >
                              {child.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ) : (
                <li key={item.to} className="flex">
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={linkClasses}
                    data-testid={`nav-${slug(item.label)}-link`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>
        </nav>

        <button
          type="button"
          data-testid="mobile-menu-button"
          className="md:hidden p-2 text-charcoal"
          aria-expanded={open}
          aria-controls="mobile-nav"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <nav id="mobile-nav" aria-label="Mobile navigation" className="md:hidden border-t border-charcoal/15 bg-cream">
          <ul className="px-6 py-4 space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    data-testid="mobile-nav-funding-link"
                    className={({ isActive }) =>
                      `block py-3 text-base transition-colors duration-300 ${
                        isActive || fundingActive ? "text-ochre-dark" : "text-charcoal hover:text-ochre-dark"
                      }`
                    }
                  >
                    Funding &amp; Partnerships
                  </NavLink>
                  <ul className="ml-3 mb-2 border-l border-charcoal/15 pl-4">
                    {item.children.slice(1).map((child) => (
                      <li key={child.to}>
                        <NavLink
                          to={child.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-funding-child-${slug(child.label)}`}
                          className={({ isActive }) =>
                            `block py-2 text-sm transition-colors duration-300 ${
                              isActive ? "text-ochre-dark" : "text-charcoal/80 hover:text-ochre-dark"
                            }`
                          }
                        >
                          {child.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${slug(item.label)}-link`}
                    className={({ isActive }) =>
                      `block py-3 text-base transition-colors duration-300 ${
                        isActive ? "text-ochre-dark" : "text-charcoal hover:text-ochre-dark"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              )
            )}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Header;
